import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Clear from '@mui/icons-material/Clear'
import ExpandMore from '@mui/icons-material/ExpandMore'
import BrandAutoComplete from './BrandAutoComplete'

const AssetDescriptors = ({
  updateParentState = () => { },
  selectedAssetGoldMetadata = {},
  storedCatalog = {},
}) => {
  const [isUGC, setIsUGC] = useState(false)
  const [isGenAiReady, setIsGenAiReady] = useState(false)
  const [keywords, setKeywords] = useState([])

  const { location: locationSuggestions = [] } = storedCatalog ?? {}

  // Initialize from gold metadata when available
  useEffect(() => {
    const metadata = selectedAssetGoldMetadata?.metadata || {}
    const isUGC = selectedAssetGoldMetadata?.is_ugc || false
    const isGenAiReady = selectedAssetGoldMetadata?.is_gen_ai_ready || false
    const assetDescriptions = metadata?.asset_descriptions || {}

    if (isUGC !== undefined) {
      setIsUGC(isUGC)
    }
    if (isGenAiReady !== undefined) {
      setIsGenAiReady(isGenAiReady)
    }
    if (Array.isArray(assetDescriptions.keywords) && assetDescriptions.keywords.length > 0) {
      setKeywords(assetDescriptions.keywords)
    }
  }, [selectedAssetGoldMetadata])

  const handleBrandChange = useCallback(
    (name) => {
      updateParentState({
        userDefinedBrandName: name,
        unsavedChanges: true,
      })
    },
    [updateParentState],
  )

  const handleUGCChange = useCallback(
    (event) => {
      const checked = event.target.checked
      setIsUGC(checked)
      updateParentState({
        assetDetails: {
          isUGC: checked,
          isGenAiReady,
          keyWords: keywords,
        },
        unsavedChanges: true,
      })
    },
    [updateParentState, isGenAiReady, keywords],
  )

  const handleKeywordsChange = useCallback(
    (_, newValue) => {
      const unique = Array.from(
        new Set(
          newValue
            .map(
              (kw) =>
                kw.charAt(0).toUpperCase() + kw.slice(1).toLowerCase(),
            )
            .filter(Boolean),
        ),
      )
      setKeywords(unique)
      updateParentState({
        assetDetails: {
          keyWords: unique,
          isUGC,
          isGenAiReady,
        },
        unsavedChanges: true,
      })
    },
    [updateParentState, isUGC, isGenAiReady],
  )

  const handleGenAiReadyChange = useCallback(
    (event) => {
      const checked = event.target.checked
      setIsGenAiReady(checked)
      updateParentState({
        assetDetails: {
          isUGC,
          isGenAiReady: checked,
          keyWords: keywords,
        },
        unsavedChanges: true,
      })
    },
    [updateParentState, isUGC, keywords],
  )

  const handleDeleteKeyword = useCallback(
    (chipToDelete) => () => {
      setKeywords((prev) => {
        const updated = prev.filter((k) => k !== chipToDelete)
        updateParentState({
          assetDetails: {
            keyWords: updated,
            isUGC,
            isGenAiReady,
          },
          unsavedChanges: true,
        })
        return updated
      })
    },
    [updateParentState, isUGC, isGenAiReady],
  )

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="asset-descriptors-content"
        id="asset-descriptors-header"
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
          Descriptors
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        {/* Brand */}
        <BrandAutoComplete
          productInformation={selectedAssetGoldMetadata?.metadata?.item_info || []}
          brand={
            selectedAssetGoldMetadata?.metadata?.item_info?.[0]?.brand || ''
          }
          newMetaGold={true}
          autoCompleteLabel="Brand"
          updateSelectedValue={handleBrandChange}
        />

        {/* Is UGC and Is Gen AI Ready */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '24px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={isUGC}
                onChange={handleUGCChange}
                data-cy="isUGCToggle"
              />
            }
            label="Is UGC (User Generated Content)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={isGenAiReady}
                onChange={handleGenAiReadyChange}
                data-cy="isGenAiReadyToggle"
              />
            }
            label="Is Gen AI Ready"
          />
        </Box>

        {/* User Keywords */}
        <FormControl variant="standard" fullWidth>
          <Autocomplete
            multiple
            freeSolo
            fullWidth
            options={locationSuggestions.map(
              (option) =>
                option.charAt(0).toUpperCase() +
                option.slice(1).toLowerCase(),
            )}
            value={keywords}
            onChange={handleKeywordsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  onDelete={handleDeleteKeyword(option)}
                  sx={{
                    margin: '0 0.2rem',
                    backgroundColor: '#C8E6C9',
                    color: '#2E7D32',
                  }}
                  deleteIcon={
                    <Clear
                      style={{
                        width: '16px',
                        height: '16px',
                        color: '#2E7D32',
                      }}
                    />
                  }
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Keywords"
                placeholder="Add keywords"
                variant="outlined"
                helperText="Press Enter after adding each keyword"
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: '#d32f2f',
                  },
                }}
              />
            )}
          />
        </FormControl>
      </AccordionDetails>
    </Accordion>
  )
}

AssetDescriptors.propTypes = {
  updateParentState: PropTypes.func,
  selectedAssetGoldMetadata: PropTypes.object,
  storedCatalog: PropTypes.object,
}

export default AssetDescriptors
